import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Dernier rempart contre l'écran noir.
 *
 * Une erreur pendant le rendu fait démonter tout l'arbre par React : la page
 * disparaît, il ne reste que le fond, et rien n'indique ce qui s'est passé.
 * C'est exactement ce qui est arrivé — une réponse d'API dont la forme avait
 * changé, et un back-office devenu noir une seconde après la connexion.
 *
 * Afficher le message ne répare rien, mais transforme une panne muette en une
 * panne qu'on peut rapporter.
 */

type Props = { children: ReactNode };
type State = { message: string | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { message: null };

  static getDerivedStateFromError(error: unknown): State {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    // Le journal du navigateur garde la pile complète, que le message seul ne
    // rend pas.
    console.error("Erreur de rendu", error, info.componentStack);
  }

  override render() {
    if (this.state.message === null) return this.props.children;

    return (
      <main style={{ maxWidth: 620, marginTop: "10vh" }}>
        <section className="card">
          <h2>Quelque chose s'est cassé</h2>
          <p className="muted">
            L'affichage s'est interrompu. Rechargez la page ; si cela se reproduit, ce
            message est ce qu'il faut rapporter.
          </p>
          <p className="error">{this.state.message}</p>
          <div className="row">
            <button className="primary" onClick={() => window.location.reload()}>
              Recharger
            </button>
          </div>
        </section>
      </main>
    );
  }
}
